import React from "react";
import { graphql } from "react-relay";

import { useAISuggestionsState } from "~/actionItems/actionItemGroup/aiSuggestionsUtils";
import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import { Button, Checkbox, useModals } from "~/ui";
import { Modal, Switch } from "~/ui/legacyFellowIndex";

import { Divider, Label, Row, SubLabel } from "./shared";

import type { AutoAcceptActionItemsSetAutoAcceptMutation } from "~/__generated__/AutoAcceptActionItemsSetAutoAcceptMutation.graphql";

import styles from "./MeetingCopilot.module.css";

function updateAutoAccept(autoAcceptEnabled: boolean, acceptAllExisting?: boolean) {
	return commitMutationPromise<AutoAcceptActionItemsSetAutoAcceptMutation>(environment, {
		mutation: graphql`
			mutation AutoAcceptActionItemsSetAutoAcceptMutation($enabled: Boolean!, $acceptAllExisting: Boolean) {
				setAutoAcceptForUser(autoAcceptEnabled: $enabled, acceptAllExisting: $acceptAllExisting) {
					viewer {
						autoAcceptAiActionItems
					}
				}
			}
		`,
		variables: { enabled: autoAcceptEnabled, acceptAllExisting },
	});
}

function AutoAcceptConfirmationModal({ close }: { close: () => void }) {
	const [applyToCurrentSuggestions, setApplyToCurrentSuggestions] = React.useState(true);

	const { hideAISuggestions } = useAISuggestionsState();

	const handleApplyToCurrentSuggestionsChange = React.useCallback((checked: boolean | "indeterminate") => {
		setApplyToCurrentSuggestions(!!checked);
	}, []);

	const handleConfirm = React.useCallback(() => {
		if (applyToCurrentSuggestions) {
			hideAISuggestions();
		}
		updateAutoAccept(true, applyToCurrentSuggestions);
		close();
	}, [applyToCurrentSuggestions, close, hideAISuggestions]);

	const handleCancel = React.useCallback(() => {
		close();
	}, [close]);

	return (
		<Modal onClose={handleCancel} width="31.25rem">
			<Modal.Header onClose={handleCancel}>
				<Modal.Title>Auto-accept my AI-suggested action items</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<p>
					Fellow will automatically turn new AI suggestions into action items, adding them to your Action Items page and
					syncing them with any active integrations.
				</p>
				<div className={styles.checkboxContainer}>
					<Checkbox
						checked={applyToCurrentSuggestions}
						onCheckedChange={handleApplyToCurrentSuggestionsChange}
						size="small"
					/>
					<span className={styles.checkboxLabel}>Apply to current AI suggestions (this may take a few seconds)</span>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<Button size="normal" onClick={handleCancel}>
					Cancel
				</Button>
				<Button size="normal" variant="primary" onClick={handleConfirm}>
					Confirm
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

export default function AutoAcceptActionItems({ userAutoAcceptOn }: { userAutoAcceptOn: boolean }) {
	const modals = useModals();

	const { resetAISuggestionsHiding } = useAISuggestionsState();

	const handleAutoAcceptActionItemsChange = React.useCallback(
		(enabled: boolean) => {
			if (enabled) {
				// Show confirmation modal when turning ON
				modals.open(props => <AutoAcceptConfirmationModal {...props} />);
			} else {
				// Allow turning OFF without confirmation
				updateAutoAccept(false);
				// We want to make sure we aren't still hiding the AI Suggestions
				resetAISuggestionsHiding();
			}
		},
		[modals, resetAISuggestionsHiding],
	);

	return (
		<>
			<Divider />
			<Row>
				<Row.Left>
					<Label>Auto-accept my AI-suggested action items</Label>
					<SubLabel>
						Automatically turn AI suggestions assigned to me into action items. Turn this off to review and accept them
						manually.
					</SubLabel>
				</Row.Left>
				<Row.Right>
					<Switch size="small" checked={userAutoAcceptOn} onCheckedChange={handleAutoAcceptActionItemsChange} />
				</Row.Right>
			</Row>
		</>
	);
}
